import { useEffect, useRef, useState } from 'react'
import type { EventData } from '@/types'

interface UseEventSourceOptions {
  onMessage?: (data: EventData) => void
  onError?: (error: Event) => void
  onOpen?: (event: Event) => void
  reconnectInterval?: number
  maxReconnectAttempts?: number
}

export const useEventSource = (url: string, options: UseEventSourceOptions = {}) => {
  const {
    onMessage,
    onError,
    onOpen,
    reconnectInterval = 5000,
    maxReconnectAttempts = 5
  } = options

  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected')
  const [lastMessage, setLastMessage] = useState<EventData | null>(null)
  const [reconnectAttempt, setReconnectAttempt] = useState(0)

  const eventSourceRef = useRef<EventSource | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const connect = () => {
    if (eventSourceRef.current?.readyState === EventSource.OPEN) {
      return
    }

    setConnectionState('connecting')

    try {
      const eventSource = new EventSource(url)
      eventSourceRef.current = eventSource

      eventSource.onopen = (event) => {
        setConnectionState('connected')
        setReconnectAttempt(0)
        onOpen?.(event)
      }

      eventSource.onmessage = (event) => {
        try {
          const data: EventData = JSON.parse(event.data)
          setLastMessage(data)
          onMessage?.(data)
        } catch (error) {
          console.error('Failed to parse SSE message:', error)
        }
      }

      eventSource.onerror = (event) => {
        setConnectionState('error')
        onError?.(event)

        if (reconnectAttempt < maxReconnectAttempts) {
          setReconnectAttempt(prev => prev + 1)
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect()
          }, reconnectInterval)
        } else {
          setConnectionState('disconnected')
          disconnect()
        }
      }
    } catch (error) {
      setConnectionState('error')
      console.error('Failed to create EventSource:', error)
    }
  }

  const disconnect = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }

    setConnectionState('disconnected')
  }

  const reconnect = () => {
    disconnect()
    setReconnectAttempt(0)
    connect()
  }

  useEffect(() => {
    connect()

    return () => {
      disconnect()
    }
  }, [url])

  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [])

  return {
    connectionState,
    lastMessage,
    reconnectAttempt,
    connect,
    disconnect,
    reconnect,
    isConnected: connectionState === 'connected',
    isConnecting: connectionState === 'connecting',
    hasError: connectionState === 'error'
  }
}